'use memo';

import { Fragment, type ReactNode, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { AccordionButton } from '@/components/Button/AccordionButton';
import Icon from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import { Loader } from '@/components/Loading';
import NavigationCard, {
  type NavigationSectionGroup,
} from '@/components/Navigation/NavigationCard';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { CForm } from './CForm';
import { useCFormStore } from './cform-store';
import type { EditButtonGroupProps } from './cform-types';
import { CFormSection } from './CFormSection/CFormSection';
import {
  type InternalSectionGroup,
  buildGroupedByParentNavigationGroups,
  buildGroupedNavigationGroups,
  buildNavigationGroups,
  groupByKey,
  toSectionHash,
} from './navigation-group-builder';

export type RouteLinkTo = NavigationSectionGroup['to'];

export interface CFormSectionDetailLayoutSection {
  sectionKey: string;
  name: string;
  displayOrder: number;
  sectionParentKey?: string | null;
  sectionParentName?: string | null;
}

export interface CFormSectionDetailLayoutProps<
  S extends CFormSectionDetailLayoutSection,
> {
  /**
   * undefined = データ未取得（何も表示しない）、
   * [] = 空（CFormEmptyState を表示）
   */
  sections: S[] | undefined;
  isLoading: boolean;
  error: Error | null;
  linkTo: RouteLinkTo;
  onSave?: () => Promise<void>;
  onReset?: () => void;
  renderSectionContent: (section: S) => ReactNode;
  /** onSave 指定時に特定セクションのみ読み取り専用にする場合に使用 */
  readonlySectionKeys?: ReadonlySet<string>;
  /**
   * セクション単位でカスタム編集ボタンを差し替える。
   * readonlySectionKeys に含まれるセクションにカスタムボタンを置きたい場合などに使う。
   * 戻り値の component は `EditButtonGroupProps.display` が false でも表示してよい
   * （hideActionButtons の影響を受けない）。
   */
  getEditButtonGroupComponent?: (
    section: S,
  ) => React.ComponentType<EditButtonGroupProps> | undefined;

  /** CForm の editUnit（default: "section"） */
  editUnit?: 'section' | 'all';
  /** CForm の itemLayout（default: "card"） */
  itemLayout?: 'label' | 'card';
  /** サイドバーナビを表示するか（default: true）。タブ内利用などナビ不要時に false */
  showNavigation?: boolean;

  /** 同一 sectionKey のセクションをグループ化して 1 CFormSection にまとめる */
  groupBySectionKey?: boolean;
  /** グループ化時、複数インスタンスを許可する sectionKey を判定 */
  isMultiSectionAllowed?: (key: string) => boolean;
  /** グループ化時、セクション追加コールバック */
  onAddSection?: (sectionKey: string) => void;
  /** グループ化時、セクション削除コールバック */
  onRemoveSection?: (sectionKey: string, displayOrder: number) => void;
  /** グループ化時のマルチセクション表示タイプ（default: "default"） */
  multiSectionDisplayType?: 'default' | 'accordion';
  /** accordion 表示時のカスタムトリガー */
  renderAccordionTrigger?: (section: S, index: number) => JSX.Element;
  /** ナビゲーション（目次）を sectionParentKey で親カテゴリ単位にグループ化する。
   *  groupBySectionKey=true と併用可能。 */
  groupNavigationBySectionParent?: boolean;
}

/**
 * Section/Attribute 詳細画面の共通レイアウト。
 *
 * ローディング / エラー / 空状態 / サイドバーナビ + CForm セクション描画を統一的に処理する。
 * onSave が未指定の場合は全セクション読み取り専用として描画される。
 * セクション内容の描画は renderSectionContent で注入する。
 *
 * groupBySectionKey=true で同一 sectionKey の複数セクション（マルチセクション）を
 * 1 つの CFormSection 内にまとめ、add/remove をサポートする。
 */
export function CFormSectionDetailLayout<
  S extends CFormSectionDetailLayoutSection,
>({
  sections,
  isLoading,
  error,
  linkTo,
  onSave,
  onReset,
  renderSectionContent,
  readonlySectionKeys,
  getEditButtonGroupComponent,
  editUnit = 'section',
  itemLayout = 'card',
  showNavigation = true,
  groupBySectionKey = false,
  isMultiSectionAllowed,
  onAddSection,
  onRemoveSection,
  multiSectionDisplayType,
  renderAccordionTrigger,
  groupNavigationBySectionParent = false,
}: CFormSectionDetailLayoutProps<S>) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'features.cForm.sectionDetailLayout',
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <VStack className="gap-4 items-center">
          <Text variant="h3" className="text-error-on-neutral-fill">
            {t('errorTitle')}
          </Text>
          <Text variant="p" className="text-neutral-muted-on-fill">
            {error.message}
          </Text>
        </VStack>
      </div>
    );
  }

  if (!sections) {
    return null;
  }

  const sortedSections = [...sections].sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );

  if (sortedSections.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-24">
        <Text variant="h3" className="text-neutral-on-fill">
          {t('emptyTitle')}
        </Text>
        <Text variant="p" className="text-neutral-muted-on-fill">
          {t('emptyDescription')}
        </Text>
      </div>
    );
  }

  const formContent = (
    <CForm
      editUnit={editUnit}
      itemLayout={itemLayout}
      bare={!showNavigation}
      onSave={onSave}
      onReset={onReset}
    >
      {groupBySectionKey ? (
        <GroupedSectionsRenderer
          sections={sortedSections}
          renderSectionContent={renderSectionContent}
          readonlySectionKeys={readonlySectionKeys}
          hasOnSave={!!onSave}
          isMultiSectionAllowed={isMultiSectionAllowed}
          onAddSection={onAddSection}
          onRemoveSection={onRemoveSection}
          multiSectionDisplayType={multiSectionDisplayType}
          renderAccordionTrigger={renderAccordionTrigger}
        />
      ) : (
        <FlatSectionsRenderer
          sections={sortedSections}
          renderSectionContent={renderSectionContent}
          readonlySectionKeys={readonlySectionKeys}
          hasOnSave={!!onSave}
          getEditButtonGroupComponent={getEditButtonGroupComponent}
        />
      )}
    </CForm>
  );

  if (!showNavigation) {
    return formContent;
  }

  return (
    <HStack className="h-full">
      <nav className="sticky top-8 h-fit w-60 shrink-0 p-8">
        <SectionNavigation
          sections={sortedSections}
          linkTo={linkTo}
          groupBySectionKey={groupBySectionKey}
          groupNavigationBySectionParent={groupNavigationBySectionParent}
        />
      </nav>
      <div className="flex-1 overflow-y-auto">{formContent}</div>
    </HStack>
  );
}

// ---------------------------------------------------------------------------
// Flat sections renderer (default mode: 1 CFormSection per section)
// ---------------------------------------------------------------------------

function FlatSectionsRenderer<S extends CFormSectionDetailLayoutSection>({
  sections,
  renderSectionContent,
  readonlySectionKeys,
  hasOnSave,
  getEditButtonGroupComponent,
}: {
  sections: S[];
  renderSectionContent: (section: S) => ReactNode;
  readonlySectionKeys?: ReadonlySet<string>;
  hasOnSave: boolean;
  getEditButtonGroupComponent?: (
    section: S,
  ) => React.ComponentType<EditButtonGroupProps> | undefined;
}) {
  return (
    <VStack className="w-full gap-4">
      {sections.map((section) => {
        const sectionHash = toSectionHash(section.sectionKey);
        const customEditButton = getEditButtonGroupComponent?.(section);
        const isReadonly =
          !hasOnSave || readonlySectionKeys?.has(section.sectionKey);
        // カスタム編集ボタン指定時は、readonly でもボタンを表示する
        const hideActionButtons = customEditButton != null ? false : isReadonly;
        return (
          <CFormSection
            key={section.sectionKey}
            id={sectionHash}
            label={section.name}
            hideActionButtons={hideActionButtons}
            editButtonGroupComponent={customEditButton}
          >
            {renderSectionContent(section)}
          </CFormSection>
        );
      })}
    </VStack>
  );
}

// ---------------------------------------------------------------------------
// Grouped sections renderer (groupBySectionKey mode)
// ---------------------------------------------------------------------------

function GroupedSectionsRenderer<S extends CFormSectionDetailLayoutSection>({
  sections,
  renderSectionContent,
  readonlySectionKeys,
  hasOnSave,
  isMultiSectionAllowed,
  onAddSection,
  onRemoveSection,
  multiSectionDisplayType,
  renderAccordionTrigger,
}: {
  sections: S[];
  renderSectionContent: (section: S) => ReactNode;
  readonlySectionKeys?: ReadonlySet<string>;
  hasOnSave: boolean;
  isMultiSectionAllowed?: (key: string) => boolean;
  onAddSection?: (sectionKey: string) => void;
  onRemoveSection?: (sectionKey: string, displayOrder: number) => void;
  multiSectionDisplayType?: 'default' | 'accordion';
  renderAccordionTrigger?: (section: S, index: number) => JSX.Element;
}) {
  const cFormStore = useCFormStore(
    useShallow((store) => ({
      editingSectionId: store.state.editingSectionId,
      isEditingAll: store.state.isEditingAll,
      isSectionEditing: store.state.isSectionEditing,
    })),
    { allowNoContext: true },
  );

  const groups = useMemo(() => groupByKey(sections), [sections]);

  return (
    <VStack className="w-full gap-4">
      {groups.map((group) => {
        const sectionHash = toSectionHash(group.sectionKey);
        const isReadonly =
          !hasOnSave || readonlySectionKeys?.has(group.sectionKey);
        const isSectionEditing =
          !isReadonly &&
          cFormStore?.isSectionEditing({ sectionId: sectionHash });
        const isMultiAllowed =
          isMultiSectionAllowed?.(group.sectionKey) ?? false;
        const showAddButton = !isReadonly && isMultiAllowed && isSectionEditing;

        return (
          <CFormSection
            key={group.sectionKey}
            id={sectionHash}
            label={group.sectionName}
            hideActionButtons={isReadonly}
            hideActionButtonUntilHover={false}
            actionButton={
              showAddButton && onAddSection ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onAddSection(group.sectionKey)}
                >
                  <Icon iconName="PlusIcon" className="w-4 h-4 mr-2" />
                  Add new
                </Button>
              ) : undefined
            }
          >
            <GroupedSectionContent
              group={group}
              isReadonly={isReadonly ?? false}
              isEditing={isSectionEditing ?? false}
              onRemoveSection={onRemoveSection}
              renderSectionContent={renderSectionContent}
              multiSectionDisplayType={multiSectionDisplayType}
              renderAccordionTrigger={renderAccordionTrigger}
            />
          </CFormSection>
        );
      })}
    </VStack>
  );
}

function GroupedSectionContent<S extends CFormSectionDetailLayoutSection>({
  group,
  isReadonly,
  isEditing,
  onRemoveSection,
  renderSectionContent,
  multiSectionDisplayType = 'default',
  renderAccordionTrigger,
}: {
  group: InternalSectionGroup<S>;
  isReadonly: boolean;
  isEditing: boolean;
  onRemoveSection?: (sectionKey: string, displayOrder: number) => void;
  renderSectionContent: (section: S) => ReactNode;
  multiSectionDisplayType?: 'default' | 'accordion';
  renderAccordionTrigger?: (section: S, index: number) => JSX.Element;
}) {
  if (group.sections.length === 1) {
    return <>{renderSectionContent(group.sections[0])}</>;
  }

  if (multiSectionDisplayType === 'accordion') {
    return (
      <AccordionGroupContent
        group={group}
        renderSectionContent={renderSectionContent}
        renderAccordionTrigger={renderAccordionTrigger}
      />
    );
  }

  return (
    <>
      {group.sections.map((section, index) => (
        <Fragment key={`${section.sectionKey}-${section.displayOrder}`}>
          <div className="col-span-7 pt-4 pb-2">
            <HStack className="justify-between items-center">
              <Text variant="h4">
                {group.sectionName} {index + 1}
              </Text>
              {group.sections.length > 1 &&
                !isReadonly &&
                isEditing &&
                onRemoveSection && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      onRemoveSection(section.sectionKey, section.displayOrder)
                    }
                  >
                    <Icon iconName="Trash2Icon" className="w-4 h-4" />
                  </Button>
                )}
            </HStack>
          </div>
          {renderSectionContent(section)}
        </Fragment>
      ))}
    </>
  );
}

function AccordionGroupContent<S extends CFormSectionDetailLayoutSection>({
  group,
  renderSectionContent,
  renderAccordionTrigger,
}: {
  group: InternalSectionGroup<S>;
  renderSectionContent: (section: S) => ReactNode;
  renderAccordionTrigger?: (section: S, index: number) => JSX.Element;
}) {
  const [primary, ...collapsed] = group.sections;

  if (collapsed.length === 0) {
    return <>{renderSectionContent(primary)}</>;
  }

  return (
    <>
      {renderSectionContent(primary)}

      <div className="col-span-7 border-t border-neutral-border my-2" />

      {collapsed.map((section, index) => (
        <div key={section.displayOrder} className="col-span-7">
          <AccordionButton
            variant="simple"
            icon={
              renderAccordionTrigger
                ? renderAccordionTrigger(section, index + 1)
                : undefined
            }
            label={
              renderAccordionTrigger
                ? undefined
                : `${group.sectionName} ${index + 2}`
            }
          >
            <div className="grid grid-cols-7 max-md:grid-cols-1 gap-5 max-md:gap-3">
              {renderSectionContent(section)}
            </div>
          </AccordionButton>
        </div>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

function SectionNavigation<S extends CFormSectionDetailLayoutSection>({
  sections,
  linkTo,
  groupBySectionKey,
  groupNavigationBySectionParent,
}: {
  sections: S[];
  linkTo: RouteLinkTo;
  groupBySectionKey: boolean;
  groupNavigationBySectionParent: boolean;
}) {
  const groups = useMemo(() => {
    if (groupBySectionKey && groupNavigationBySectionParent)
      return buildGroupedByParentNavigationGroups(sections, linkTo);
    if (groupBySectionKey)
      return buildGroupedNavigationGroups(sections, linkTo);
    return buildNavigationGroups(sections, linkTo);
  }, [sections, linkTo, groupBySectionKey, groupNavigationBySectionParent]);

  return (
    <NavigationCard
      sections={groups}
      showGroupSeparator={groupNavigationBySectionParent}
    />
  );
}
